import { TagCategory, Tag, TagCategoryWithTags } from '../types';
import { supabase } from '../lib/supabase';

// --- Helpers ---

function mapCategory(row: any): TagCategory {
  return {
    id: row.id,
    tenantId: row.tenant_id,
    name: row.name,
    description: row.description,
    sortOrder: row.sort_order,
  };
}

function mapTag(row: any): Tag {
  return {
    id: row.id,
    categoryId: row.category_id,
    tenantId: row.tenant_id,
    name: row.name,
    color: row.color,
    sortOrder: row.sort_order,
  };
}

// --- Service ---

export const tagService = {

  /** Fetch all tag categories with their tags for a tenant */
  async getCategoriesWithTags(tenantId: string): Promise<TagCategoryWithTags[]> {
    const { data, error } = await supabase
      .from('tag_categories')
      .select('*, tags(*)')
      .eq('tenant_id', tenantId)
      .order('sort_order');

    if (error) throw error;

    return (data || []).map((row: any) => ({
      ...mapCategory(row),
      tags: (row.tags || [])
        .map(mapTag)
        .sort((a: Tag, b: Tag) => a.sortOrder - b.sortOrder),
    }));
  },

  // --- CATEGORY CRUD ---

  async createCategory(tenantId: string, data: { name: string; description?: string }): Promise<TagCategory> {
    // Get max sort_order for this tenant
    const { data: existing } = await supabase
      .from('tag_categories')
      .select('sort_order')
      .eq('tenant_id', tenantId)
      .order('sort_order', { ascending: false })
      .limit(1);

    const nextOrder = existing && existing.length > 0 ? existing[0].sort_order + 1 : 0;

    const { data: row, error } = await supabase
      .from('tag_categories')
      .insert({
        tenant_id: tenantId,
        name: data.name,
        description: data.description || null,
        sort_order: nextOrder,
      })
      .select()
      .single();

    if (error) throw error;
    return mapCategory(row);
  },

  async updateCategory(id: string, data: Partial<{ name: string; description: string; sortOrder: number }>): Promise<TagCategory> {
    const updates: Record<string, any> = {};
    if (data.name !== undefined) updates.name = data.name;
    if (data.description !== undefined) updates.description = data.description;
    if (data.sortOrder !== undefined) updates.sort_order = data.sortOrder;

    const { data: row, error } = await supabase
      .from('tag_categories')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return mapCategory(row);
  },

  async deleteCategory(id: string): Promise<void> {
    const { error } = await supabase
      .from('tag_categories')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  // --- TAG CRUD ---

  async createTag(categoryId: string, tenantId: string, data: { name: string; color?: string }): Promise<Tag> {
    // Get max sort_order for this category
    const { data: existing } = await supabase
      .from('tags')
      .select('sort_order')
      .eq('category_id', categoryId)
      .order('sort_order', { ascending: false })
      .limit(1);

    const nextOrder = existing && existing.length > 0 ? existing[0].sort_order + 1 : 0;

    const { data: row, error } = await supabase
      .from('tags')
      .insert({
        category_id: categoryId,
        tenant_id: tenantId,
        name: data.name,
        color: data.color || null,
        sort_order: nextOrder,
      })
      .select()
      .single();

    if (error) throw error;
    return mapTag(row);
  },

  async updateTag(id: string, data: Partial<{ name: string; color: string; sortOrder: number }>): Promise<Tag> {
    const updates: Record<string, any> = {};
    if (data.name !== undefined) updates.name = data.name;
    if (data.color !== undefined) updates.color = data.color;
    if (data.sortOrder !== undefined) updates.sort_order = data.sortOrder;

    const { data: row, error } = await supabase
      .from('tags')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return mapTag(row);
  },

  async deleteTag(id: string): Promise<void> {
    const { error } = await supabase
      .from('tags')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  // --- COURSE TAGS ---

  /** Fetch full tag rows for a single course (used on CourseDetail). */
  async getTagsForCourse(courseId: string): Promise<Tag[]> {
    const { data, error } = await supabase
      .from('course_tags')
      .select('tag:tags(*)')
      .eq('course_id', courseId);

    if (error) throw error;
    return (data || [])
      .map((row: any) => row.tag)
      .filter(Boolean)
      .map(mapTag);
  },

  /**
   * Batch-fetch tags for many courses in one round-trip.
   * Returns a record keyed by courseId. Used for catalog grids.
   */
  async getTagsForCourses(courseIds: string[]): Promise<Record<string, Tag[]>> {
    if (courseIds.length === 0) return {};

    const { data, error } = await supabase
      .from('course_tags')
      .select('course_id, tag:tags(*)')
      .in('course_id', courseIds);

    if (error) throw error;

    const result: Record<string, Tag[]> = {};
    for (const row of data || []) {
      if (!row.tag) continue;
      const tag = mapTag(row.tag);
      (result[row.course_id] ??= []).push(tag);
    }
    // Sort each course's tags by sortOrder for stable display
    for (const cid of Object.keys(result)) {
      result[cid].sort((a, b) => a.sortOrder - b.sortOrder);
    }
    return result;
  },

  /**
   * Public-catalog variant: fetch categories + tags via anon-readable RLS.
   * Only returns tags/categories actually used on published courses (per
   * migration 20260428000001). Filters to tenant on the client side.
   */
  async getPublicCategoriesWithTags(tenantId: string): Promise<TagCategoryWithTags[]> {
    return this.getCategoriesWithTags(tenantId);
  },

  async getCourseTagIds(courseId: string): Promise<string[]> {
    const { data, error } = await supabase
      .from('course_tags')
      .select('tag_id')
      .eq('course_id', courseId);

    if (error) throw error;
    return (data || []).map((row: any) => row.tag_id);
  },

  async setCourseTags(courseId: string, tenantId: string, tagIds: string[]): Promise<void> {
    // Delete existing tags for this course
    const { error: deleteError } = await supabase
      .from('course_tags')
      .delete()
      .eq('course_id', courseId);

    if (deleteError) throw deleteError;

    // Insert new tags
    if (tagIds.length > 0) {
      const rows = tagIds.map(tagId => ({
        course_id: courseId,
        tag_id: tagId,
        tenant_id: tenantId,
      }));

      const { error: insertError } = await supabase
        .from('course_tags')
        .insert(rows);

      if (insertError) throw insertError;
    }
  },
};
